/**
 * usePageTitle Hook
 * Returns the page title based on the current route.
 * @packageDocumentation
 */

import { useLocation } from 'react-router';

/** Route-to-title mapping for all application pages. */
const routeTitles: Record<string, string> = {
  '/mappings': 'Mappings',
  '/dashboard': 'Overview',
  '/dlq': 'Dead Letter Queue',
};

/**
 * Prefix-keyed title resolver functions for dynamic route segments.
 * Entries are matched in insertion order; more specific prefixes must come first.
 */
const prefixTitleOverrides: Record<string, (path: string) => string> = {
  '/mappings/': (path) => (path === '/mappings/new' ? 'Create Mapping' : 'Edit Mapping'),
};

/**
 * Hook that returns the page title based on the current route.
 * Routes use flat paths (no tenant prefix) so the pathname is matched directly.
 *
 * @returns { string } The page title for the current route.
 */
export const usePageTitle = (): string => {
  const { pathname } = useLocation();

  if (routeTitles[pathname]) {
    return routeTitles[pathname];
  }

  for (const [routePrefix, titleFn] of Object.entries(prefixTitleOverrides)) {
    if (pathname.startsWith(routePrefix)) {
      return titleFn(pathname);
    }
  }

  for (const [route, title] of Object.entries(routeTitles)) {
    if (pathname.startsWith(`${route}/`)) {
      return title;
    }
  }

  return 'Nexus Admin';
};
